/**
 * useTabState - 汎用的なタブ状態管理フック
 *
 * tabViewStateとの同期を自動的に行い、pane split時も状態を保持する
 */

import { useState, useEffect, useCallback } from 'react';
import { useProjectStore } from '../../../../stores/projectStore';
import type { TabState } from '../../../../../types/pane.types';
import type { TabConfig } from '../../../../../types/tabs';
import { markAsTransient } from '../../../../../types/serialization.types';

/**
 * タブの状態を管理する汎用フック
 *
 * @param tab - タブの状態
 * @param initialState - デフォルトの初期状態
 * @returns [state, setState] - React.useStateと同様のインターフェース
 *
 * @example
 * ```typescript
 * const [config, setConfig] = useTabState<CrosstabConfig>(tab, {
 *   datasetId: undefined,
 *   rowFields: [],
 *   columnFields: [],
 *   valueFields: [],
 *   options: defaultOptions
 * });
 * ```
 */
export function useTabState<T>(
  tab: TabState,
  initialState: T
): [T, (newState: T | ((prevState: T) => T)) => void] {
  const updateTabState = useProjectStore((state) => state.updateTabState);
  const findTab = useProjectStore((state) => state.findTab);

  // 初期化: tabViewStateから読み込む、なければinitialStateを使用
  const [state, setState] = useState<T>(() => {
    const savedState = tab.tabViewState as T | undefined;
    return savedState ?? initialState;
  });

  // 状態をtabViewStateに同期保存
  useEffect(() => {
    const tabInfo = findTab(tab.id);
    if (!tabInfo) return;

    // 状態をシャローコピーしてからtransientマークを付ける
    let stateToSave = state as TabConfig;

    // tabState.cachedResult がある場合は transient としてマーク
    if (stateToSave && typeof stateToSave === 'object' && 'tabState' in stateToSave) {
      const configWithTabState = stateToSave as TabConfig & { tabState: Record<string, unknown> };
      const tabStateObj = configWithTabState.tabState;
      if (tabStateObj && typeof tabStateObj === 'object' && 'cachedResult' in tabStateObj) {
        // tabStateをシャローコピー
        const newTabState = { ...tabStateObj };
        stateToSave = { ...stateToSave, tabState: newTabState } as TabConfig;
        markAsTransient(newTabState, ['cachedResult']);
      }
    }

    updateTabState(tabInfo.container.id, tab.id, (tabState) => ({
      ...tabState,
      tabViewState: stateToSave
    }));
  }, [state, tab.id, findTab, updateTabState]);

  // setStateのラッパー（React.useStateと同じインターフェース）
  const setStateWrapper = useCallback((newState: T | ((prevState: T) => T)) => {
    setState(newState);
  }, []);

  return [state, setStateWrapper];
}
